import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Grid, MenuItem,
  Tabs, Tab, Box, Typography, Stack, Chip, Divider, Alert, Autocomplete,
} from "@mui/material";
import { Paperclip, Upload } from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { ticketService } from "../../api/services/ticketService";
import { slaService } from "../../api/services/slaService";
import { identityService } from "../../api/services/identityService";
import { useTheme } from "../../contexts/ThemeContext";
import { downloadBlob, formatFileSize } from "../../utils/downloadBlob";

const SOURCE_OPTIONS = ["portal", "email", "phone", "whatsapp", "api"];
// Severity is deliberately separate from Priority: Priority drives the SLA
// policy lookup (ats_sla_service.start_timer keys off priority_id).
// Severity is informational triage context and doesn't affect SLA math.
const SEVERITY_OPTIONS = ["Low", "Medium", "High", "Critical"];

/**
 * Two-step flow:
 *   1. Ticket Details -- create the ticket. Nothing below (assignment,
 *      attachments, SLA preview) can exist until the ticket has a real id,
 *      so this tab is a hard gate, not just a UI convention.
 *   2. Assign & Attach -- unlocked immediately after creation, in the same
 *      dialog. Shows the live SLA due dates computed from whatever
 *      priority was picked in step 1, lets you assign a team/employee, and
 *      lets you record attachments against the now-real ticket.
 */
export default function TicketCreateDialog({ open, onClose, onCreated, projectId }) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [tab, setTab] = useState(0);
  const [createdTicket, setCreatedTicket] = useState(null);

  const { control, handleSubmit, reset } = useForm({
    defaultValues: { subject: "", description: "", source: "portal", severity: "", priority_id: "" },
  });

  const { data: priorities } = useQuery({ queryKey: ["priorities"], queryFn: slaService.listPriorities, staleTime: Infinity });
  const { data: employees } = useQuery({ queryKey: ["employees"], queryFn: identityService.listEmployees, enabled: open });
  const { data: teams } = useQuery({ queryKey: ["teams"], queryFn: identityService.listTeams, enabled: open });

  // fresh state every time the dialog opens -- otherwise a second "New
  // Ticket" click would reopen showing the previous ticket's assign/attach tab
  useEffect(() => {
    if (open) {
      setTab(0);
      setCreatedTicket(null);
      reset({ subject: "", description: "", source: "portal", severity: "", priority_id: "" });
    }
  }, [open, reset]);

  const createMutation = useMutation({
    mutationFn: (values) =>
      ticketService.create({
        ...values,
        priority_id: values.priority_id || null,
        severity: values.severity || null,
        project_id: projectId ? Number(projectId) : null,
      }),
    onSuccess: (ticket) => {
      setCreatedTicket(ticket);
      setTab(1);
      enqueueSnackbar(`Ticket ${ticket.ticket_number} created`, { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      onCreated && onCreated(ticket);
    },
    onError: (err) => enqueueSnackbar(err.response?.data?.detail || "Failed to create ticket", { variant: "error" }),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {createdTicket ? `${createdTicket.ticket_number} — ${createdTicket.subject}` : "New Ticket"}
      </DialogTitle>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 3, borderBottom: "1px solid", borderColor: "divider" }}>
        <Tab label="1. Ticket Details" />
        <Tab label="2. Assign & Attach" disabled={!createdTicket} />
      </Tabs>

      <DialogContent sx={{ pt: 3 }}>
        {tab === 0 && (
          <DetailsTab control={control} priorities={priorities} readOnly={Boolean(createdTicket)} />
        )}
        {tab === 1 && createdTicket && (
          <AssignAttachTab ticket={createdTicket} employees={employees} teams={teams} />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {tab === 0 && !createdTicket && (
          <>
            <Button onClick={onClose} color="inherit">Cancel</Button>
            <Button variant="contained" disabled={createMutation.isPending} onClick={handleSubmit((v) => createMutation.mutate(v))}>
              {createMutation.isPending ? "Creating..." : "Create & Continue"}
            </Button>
          </>
        )}
        {tab === 0 && createdTicket && (
          <Button variant="contained" onClick={() => setTab(1)}>Next: Assign & Attach</Button>
        )}
        {tab === 1 && (
          <Button variant="contained" onClick={onClose}>Done</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function DetailsTab({ control, priorities, readOnly }) {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Controller
          name="subject" control={control} rules={{ required: true }}
          render={({ field, fieldState }) => (
            <TextField {...field} label="Subject" fullWidth required disabled={readOnly} error={fieldState.invalid} />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          name="description" control={control}
          render={({ field }) => (
            <TextField {...field} label="Description" fullWidth multiline minRows={3} disabled={readOnly} />
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name="priority_id" control={control}
          render={({ field }) => (
            <TextField
              {...field} select label="Priority" fullWidth disabled={readOnly}
              helperText="Starts the SLA response/resolution timer"
            >
              <MenuItem value="">None</MenuItem>
              {(priorities || []).map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: p.color }} />
                    {p.name}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name="severity" control={control}
          render={({ field }) => (
            <TextField {...field} select label="Severity" fullWidth disabled={readOnly} helperText="Triage context, doesn't affect SLA">
              <MenuItem value="">None</MenuItem>
              {SEVERITY_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          name="source" control={control}
          render={({ field }) => (
            <TextField {...field} select label="Source" fullWidth disabled={readOnly}>
              {SOURCE_OPTIONS.map((s) => <MenuItem key={s} value={s} sx={{ textTransform: "capitalize" }}>{s}</MenuItem>)}
            </TextField>
          )}
        />
      </Grid>
    </Grid>
  );
}

function AssignAttachTab({ ticket, employees, teams }) {
  const { T } = useTheme();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef(null);

  const [team, setTeam] = useState(() => (teams || []).find((t) => t.id === ticket.team_id) || null);
  const [assignee, setAssignee] = useState(() => (employees || []).find((e) => e.id === ticket.assignee_employee_id) || null);
  const [selectedFile, setSelectedFile] = useState(null);

  // Once a team is picked, only that team's members make sense as an assignee --
  // narrows the searchable dropdown instead of showing everyone in the company.
  const assigneeOptions = useMemo(() => {
    if (!team) return employees || [];
    return (employees || []).filter((e) => team.member_employee_ids.includes(e.id));
  }, [team, employees]);

  const { data: sla } = useQuery({
    queryKey: ["ticket-sla-preview", ticket.id],
    queryFn: () => ticketService.sla(ticket.id),
    retry: false,
  });

  const { data: attachments } = useQuery({
    queryKey: ["ticket-attachments-preview", ticket.id],
    queryFn: () => ticketService.listAttachments(ticket.id),
  });

  // Saves immediately on every Team/Assignee change -- there's no separate
  // "Save" step, so a selection can never be silently lost by closing the
  // dialog before clicking a button that isn't there.
  const assignMutation = useMutation({
    mutationFn: (payload) => ticketService.update(ticket.id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tickets"] }),
    onError: (err) => enqueueSnackbar(err.response?.data?.detail || "Could not save assignment", { variant: "error" }),
  });

  const handleTeamChange = (value) => {
    setTeam(value);
    // dropping to a team the current assignee isn't on -- clear them together
    // so the saved payload never pairs a team with an outside assignee
    const nextAssignee = value && assignee && !value.member_employee_ids.includes(assignee.id) ? null : assignee;
    if (nextAssignee !== assignee) setAssignee(null);
    assignMutation.mutate({
      team_id: value ? value.id : null,
      assignee_employee_id: nextAssignee ? nextAssignee.id : null,
    });
  };

  const handleAssigneeChange = (value) => {
    setAssignee(value);
    assignMutation.mutate({
      team_id: team ? team.id : null,
      assignee_employee_id: value ? value.id : null,
    });
  };

  const uploadMutation = useMutation({
    mutationFn: () => ticketService.uploadAttachment(ticket.id, selectedFile),
    onSuccess: () => {
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      queryClient.invalidateQueries({ queryKey: ["ticket-attachments-preview", ticket.id] });
    },
    onError: (err) => enqueueSnackbar(err.response?.data?.detail || "Upload failed", { variant: "error" }),
  });

  const handleOpenAttachment = async (a) => {
    if (a.has_file) {
      try {
        const blob = await ticketService.downloadAttachment(ticket.id, a.id);
        downloadBlob(blob, a.file_name);
      } catch {
        enqueueSnackbar("Couldn't download file", { variant: "error" });
      }
    } else if (a.file_url) {
      window.open(a.file_url, "_blank", "noreferrer");
    }
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Assign</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Autocomplete
              size="small"
              options={teams || []}
              getOptionLabel={(t) => t.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={team}
              onChange={(_, value) => handleTeamChange(value)}
              renderInput={(params) => <TextField {...params} label="Team" placeholder="Unassigned" />}
            />
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              size="small"
              options={assigneeOptions}
              getOptionLabel={(e) => e.name || e.email}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={assignee}
              onChange={(_, value) => handleAssigneeChange(value)}
              renderInput={(params) => (
                <TextField {...params} label="Assignee" placeholder={team ? `Search ${team.name}...` : "Search employees..."} />
              )}
            />
          </Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          {assignMutation.isPending ? "Saving..." : "Saved automatically as you pick a team or assignee."}
        </Typography>
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>SLA</Typography>
        {sla ? (
          <Stack direction="row" spacing={4}>
            <Box>
              <Typography variant="caption" color="text.secondary">Response due</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {sla.response_due_at ? new Date(sla.response_due_at).toLocaleString() : "-"}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Resolution due</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {sla.resolution_due_at ? new Date(sla.resolution_due_at).toLocaleString() : "-"}
              </Typography>
            </Box>
          </Stack>
        ) : (
          <Alert severity="info" sx={{ py: 0.5 }}>
            No SLA timer — a priority wasn't set in step 1, or there's no SLA policy configured for it yet
            (see Admin → SLA Settings).
          </Alert>
        )}
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Attachments</Typography>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button component="label" size="small" variant="outlined" startIcon={<Paperclip size={14} />}>
            Choose File
            <input
              ref={fileInputRef} type="file" hidden
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
            />
          </Button>
          {selectedFile && (
            <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
              {selectedFile.name} ({formatFileSize(selectedFile.size)})
            </Typography>
          )}
          <Button
            size="small" variant="contained" startIcon={<Upload size={14} />}
            disabled={!selectedFile || uploadMutation.isPending}
            onClick={() => uploadMutation.mutate()}
          >
            {uploadMutation.isPending ? "Uploading..." : "Upload"}
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          Any document, image, or spreadsheet up to 15MB.
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
          {(attachments || []).map((a) => (
            <Chip
              key={a.id} label={a.file_name} size="small" sx={{ bgcolor: T.card2, cursor: "pointer" }}
              onClick={() => handleOpenAttachment(a)}
            />
          ))}
        </Stack>
      </Box>
    </Stack>
  );
}